import React, { useState, useEffect, useCallback } from 'react';
import { 
  Users, UserPlus, Clock, Mail, 
  Trash2, Loader2, X, Shield, Search,
  LogIn, LogOut, CalendarDays, Download, FileSpreadsheet, FileText
} from 'lucide-react';
import api from '../../services/api';
import { getAdminManagerMonthlyReport, getAdminTimersToday, downloadTimerReport, deleteUser, updateManagerGreenApiSlot } from '../../services/api';

const liveSecondsFromSync = (baseSeconds, status, activeStatus, syncedAt) => {
  const base = Number(baseSeconds) || 0;
  if (status !== activeStatus) return base;
  const elapsed = Math.floor((Date.now() - syncedAt) / 1000);
  return base + Math.max(0, elapsed);
};

const ManagersPage = () => {
  const [managers, setManagers] = useState([]);
  const [syncedAt, setSyncedAt] = useState(Date.now());
  const [, setLiveTick] = useState(0);
  const [meta, setMeta] = useState({ current_page: 1, last_page: 1, total: 0 });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [selectedManager, setSelectedManager] = useState(null);
  const [reportMonth, setReportMonth] = useState(new Date().toISOString().slice(0, 7));
  const [monthlyReport, setMonthlyReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [addManagerLoading, setAddManagerLoading] = useState(false);
  const [exportPeriod, setExportPeriod] = useState('today');
  const [exportMonth, setExportMonth] = useState(new Date().toISOString().slice(0, 7));
  const [exportLoading, setExportLoading] = useState(null); // 'excel' | 'pdf' | null
  const [deletingId, setDeletingId] = useState(null);
  const [slotUpdatingId, setSlotUpdatingId] = useState(null);
  
  const [newManager, setNewManager] = useState({
    name: '',
    email: '',
    password: '',
    role: 'manager',
    green_api_slot: ''
  });

  const fetchManagers = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const response = await getAdminTimersToday({ page, per_page: 6 });
      setManagers(response.data || []);
      setSyncedAt(Date.now());
      setMeta(response.meta || { current_page: 1, last_page: 1, total: 0 });
    } catch (err) {
      if (!silent) console.error("Ошибка при загрузке менеджеров:", err);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    fetchManagers(false);
    const interval = setInterval(() => fetchManagers(true), 15000);
    return () => clearInterval(interval);
  }, [fetchManagers]);

  useEffect(() => {
    const interval = setInterval(() => setLiveTick((value) => value + 1), 1000);
    return () => clearInterval(interval);
  }, []);

  const handleAddManager = async (e) => {
    e.preventDefault();
    setAddManagerLoading(true);
    try {
      await api.post('/users', newManager);
      setShowAddModal(false);
      setNewManager({ name: '', email: '', password: '', role: 'manager', green_api_slot: '' });
      fetchManagers();
    } catch (err) {
      const msg = err.response?.data?.message || "Ошибка при создании";
      alert(err.response?.data?.errors ? Object.values(err.response.data.errors)[0][0] : msg);
    } finally {
      setAddManagerLoading(false);
    }
  };

  const openMonthlyReport = async (manager) => {
    setSelectedManager(manager);
    setShowReportModal(true);
    setReportLoading(true);
    try {
      const data = await getAdminManagerMonthlyReport(manager.id, reportMonth);
      setMonthlyReport(data);
    } catch (err) {
      console.error("Ошибка при загрузке месячного отчета:", err);
      setMonthlyReport(null);
    } finally {
      setReportLoading(false);
    }
  };

  const handleMonthChange = async (value) => {
    setReportMonth(value);
    if (!selectedManager) return;
    setReportLoading(true);
    try {
      const data = await getAdminManagerMonthlyReport(selectedManager.id, value);
      setMonthlyReport(data);
    } catch (err) {
      console.error("Ошибка при смене месяца:", err);
      setMonthlyReport(null);
    } finally {
      setReportLoading(false);
    }
  };

  const formatTime = (seconds) => {
    if (!seconds) return "0ч 0м";
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    return `${h}ч ${m}м`;
  };

  const handleDeleteManager = async (manager) => {
    if (!window.confirm(`Удалить сотрудника «${manager.name}»? Его лиды останутся в CRM без менеджера.`)) {
      return;
    }

    setDeletingId(manager.id);
    try {
      await deleteUser(manager.id);
      fetchManagers();
    } catch (err) {
      alert(err.response?.data?.message || 'Не удалось удалить сотрудника');
    } finally {
      setDeletingId(null);
    }
  };

  const handleSlotChange = async (manager, value) => {
    setSlotUpdatingId(manager.id);
    try {
      await updateManagerGreenApiSlot(manager.id, value);
      await fetchManagers(true);
    } catch (err) {
      alert(err.response?.data?.message || Object.values(err.response?.data?.errors || {})?.[0]?.[0] || 'Не удалось закрепить WhatsApp');
    } finally {
      setSlotUpdatingId(null);
    }
  };

  const handleExport = async (format) => {
    if (!selectedManager) return;
    const needMonth = exportPeriod === 'month' || exportPeriod === '3months';
    if (needMonth && !exportMonth) {
      alert('Выберите месяц для периода «За месяц» или «За 3 месяца»');
      return;
    }
    setExportLoading(format);
    try {
      await downloadTimerReport(selectedManager.id, {
        format,
        period: exportPeriod,
        month: needMonth ? exportMonth : null,
      });
    } catch (err) {
      alert(err.response?.data?.message || 'Не удалось скачать отчёт');
    } finally {
      setExportLoading(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#0f172a] text-slate-200 p-4 sm:p-6 md:p-8">
      <div className="max-w-7xl mx-auto">
        
        {/* Заголовок */}
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-6">
          <div>
            <h1 className="text-4xl font-black tracking-tighter text-white flex items-center gap-3 italic uppercase">
            Команда
            </h1>
            <div className="flex items-center gap-2 mt-2 uppercase text-[10px] font-black tracking-[0.3em] text-indigo-400">
              <div className="h-1 w-8 bg-indigo-500 rounded-full" /> Управление доступом
            </div>
          </div>
          
          <button 
            onClick={() => setShowAddModal(true)}
            className="bg-white text-black hover:bg-indigo-600 hover:text-white px-8 py-4 rounded-[2rem] font-black text-[10px] uppercase transition-all flex items-center gap-3 shadow-xl"
          >
            <UserPlus size={18} strokeWidth={3} /> Добавить сотрудника
          </button>
        </div>

        {/* Сетка менеджеров */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {loading ? (
            <div className="col-span-full flex min-h-[320px] items-center justify-center rounded-[3rem] bg-[#1e293b]/20 border border-white/5">
              <Loader2 className="animate-spin text-indigo-500" size={40} />
            </div>
          ) : (
          managers.map((manager) => (
            <div 
              key={manager.id} 
              className="bg-[#1e293b]/40 backdrop-blur-xl border border-white/5 rounded-[3rem] p-8 relative group hover:border-indigo-500/30 transition-all shadow-2xl flex flex-col"
            >
              {/* Верхняя часть */}
              <div className="flex justify-between items-start mb-6">
                <div className="h-16 w-16 bg-gradient-to-br from-indigo-500 to-purple-700 rounded-2xl flex items-center justify-center text-2xl font-black text-white shadow-lg shadow-indigo-500/20">
                  {manager.name.charAt(0).toUpperCase()}
                </div>
                <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-[8px] font-black uppercase tracking-widest ${
                  manager.status === 'online' 
                  ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' 
                  : 'bg-slate-500/10 text-slate-500 border-white/5'
                }`}>
                  <div className={`h-1 w-1 rounded-full ${manager.status === 'online' ? 'bg-emerald-500 animate-pulse' : 'bg-slate-500'}`} />
                  {manager.status === 'online' ? 'В сети' : 'Оффлайн'}
                </div>
              </div>

              {/* Инфо */}
              <div className="mb-6">
                <h3 className="text-xl font-black text-white leading-tight">{manager.name}</h3>
                <div className="flex items-center gap-2 text-slate-500 text-[10px] mt-1 font-black uppercase tracking-widest">
                  <Shield size={10} className="text-indigo-400" /> {manager.role}
                </div>
                <select
                  value={manager.green_api_slot || ''}
                  disabled={slotUpdatingId === manager.id}
                  onChange={e => handleSlotChange(manager, e.target.value)}
                  className="mt-2 bg-slate-900 border border-white/10 rounded-lg px-2 py-1 text-[10px] font-bold text-emerald-400 outline-none disabled:opacity-50"
                >
                  <option value="">WhatsApp не закреплён</option>
                  {[1, 2, 3, 4, 5].map(slot => <option key={slot} value={slot}>WhatsApp №{slot}</option>)}
                </select>
              </div>

              {/* Таймер */}
              <div className="bg-slate-900/60 rounded-2xl p-4 border border-white/5 flex items-center justify-between mb-4">
                <div>
                  <p className="text-[8px] font-black text-slate-500 uppercase tracking-[0.2em] mb-0.5">Сегодня</p>
                  <p className="text-xl font-black text-white tabular-nums">
                    {formatTime(liveSecondsFromSync(manager.today_work_time, manager.status, 'online', syncedAt))}
                  </p>
                </div>
                <Clock size={20} className="text-indigo-500/50" />
              </div>

              {/* БЛОК ХРОНОЛОГИИ */}
              <div className="flex-1 bg-white/[0.02] rounded-2xl border border-white/5 p-4">
                <p className="text-[8px] font-black text-indigo-400 uppercase tracking-[0.2em] mb-3 flex items-center gap-2">
                  <CalendarDays size={10} /> События дня
                </p>
                {manager.today_events?.length ? (
                  <div className="space-y-2">
                    {manager.today_events.map((log) => (
                      <div key={log.id} className="flex items-center justify-between text-[10px]">
                        <div className="flex items-center gap-2">
                          {log.action === 'start_work' || log.action === 'break_end' ? (
                            <LogIn size={12} className="text-emerald-500" />
                          ) : (
                            <LogOut size={12} className="text-rose-500" />
                          )}
                          <span className="font-bold text-slate-300">{log.label}</span>
                        </div>
                        <span className="font-black text-slate-500 tabular-nums">{log.time}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-[10px] font-bold text-slate-500">Нет событий за сегодня</p>
                )}
              </div>

              {/* НЕДЕЛЬНЫЙ ОТЧЕТ */}
              <div className="mt-3 bg-white/[0.02] rounded-2xl border border-white/5 p-4">
                <p className="text-[8px] font-black text-indigo-400 uppercase tracking-[0.2em] mb-3">
                  Отчет за 7 дней
                </p>
                {manager.weekly_report?.length ? (
                  <div className="space-y-1.5">
                    {manager.weekly_report.map((day) => (
                      <div key={day.date} className="flex items-center justify-between text-[10px]">
                        <span className="font-bold text-slate-400">{day.date}</span>
                        <span className="font-black text-white tabular-nums">{formatTime(day.work_seconds ?? 0)}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-[10px] font-bold text-slate-500">Нет данных</p>
                )}
                <button
                  onClick={() => openMonthlyReport(manager)}
                  className="mt-3 w-full bg-indigo-600/80 hover:bg-indigo-500 text-white py-2 rounded-xl font-black text-[10px] uppercase tracking-[0.2em] transition-all"
                >
                  Подробнее
                </button>
              </div>

              <button
                type="button"
                onClick={() => handleDeleteManager(manager)}
                disabled={deletingId === manager.id}
                title="Удалить сотрудника"
                className="absolute top-6 right-6 opacity-70 sm:opacity-0 sm:group-hover:opacity-100 p-2 text-rose-500 hover:bg-rose-500/10 rounded-xl transition-all disabled:opacity-40"
              >
                {deletingId === manager.id ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <Trash2 size={16} />
                )}
              </button>
            </div>
          ))
          )}
        </div>

        <div className="mt-8 flex items-center justify-between">
          <p className="text-xs text-slate-500 font-bold">Всего менеджеров: {meta.total || managers.length}</p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1 || loading}
              className="px-4 py-2 rounded-xl bg-white/5 border border-white/10 disabled:opacity-50 text-[10px] font-black uppercase tracking-widest flex items-center gap-2"
            >
              {loading ? <Loader2 className="animate-spin" size={14} /> : null}
              Назад
            </button>
            <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">
              {meta.current_page} / {meta.last_page}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(meta.last_page || 1, p + 1))}
              disabled={page >= (meta.last_page || 1) || loading}
              className="px-4 py-2 rounded-xl bg-white/5 border border-white/10 disabled:opacity-50 text-[10px] font-black uppercase tracking-widest flex items-center gap-2"
            >
              Вперед
              {loading ? <Loader2 className="animate-spin" size={14} /> : null}
            </button>
          </div>
        </div>
      </div>

      {/* Модалка */}
      {showReportModal && (
        <div className="fixed inset-0 z-[210] flex items-end sm:items-center justify-center p-0 sm:p-4">
          <div className="absolute inset-0 bg-black/85 backdrop-blur-sm" onClick={() => setShowReportModal(false)} />
          <div className="relative w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col bg-[#1e293b] border border-white/10 rounded-t-2xl sm:rounded-[2rem] p-4 sm:p-6 shadow-2xl">
            <div className="flex items-center justify-between mb-5">
              <div>
                <h3 className="text-xl font-black text-white uppercase italic">Месячный отчет</h3>
                <p className="text-[10px] uppercase tracking-widest text-slate-400 font-bold mt-1">
                  {selectedManager?.name}
                </p>
              </div>
              <button onClick={() => setShowReportModal(false)} className="text-slate-500 hover:text-white">
                <X size={20} />
              </button>
            </div>

            <div className="mb-4">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2">
                Выбери месяц
              </label>
              <input
                type="month"
                value={reportMonth}
                onChange={(e) => handleMonthChange(e.target.value)}
                className="bg-slate-900 border border-white/10 rounded-xl px-4 py-2 text-sm text-white outline-none"
              />
            </div>

            {reportLoading ? (
              <div className="flex justify-center py-10">
                <Loader2 className="animate-spin text-indigo-500" size={26} />
              </div>
            ) : monthlyReport ? (
              <div className="space-y-2 max-h-[55vh] overflow-y-auto pr-1">
                {monthlyReport.days?.map((day) => (
                  <div key={day.date} className="flex items-center justify-between bg-slate-900/50 border border-white/5 rounded-xl px-4 py-2 text-sm">
                    <span className="font-bold text-slate-300">{day.date}</span>
                    <span className="font-black text-white tabular-nums">{formatTime(day.work_seconds)}</span>
                  </div>
                ))}
                <div className="mt-3 pt-3 border-t border-white/10 flex items-center justify-between">
                  <span className="text-[11px] font-black uppercase tracking-wider text-slate-400">Итого за месяц</span>
                  <span className="text-lg font-black text-white tabular-nums">{formatTime(monthlyReport.total_work_seconds)}</span>
                </div>
              </div>
            ) : (
              <p className="text-sm text-slate-500 py-6">Не удалось загрузить отчет</p>
            )}

            {/* Скачать отчёт: период + Excel / PDF */}
            <div className="mt-6 pt-6 border-t border-white/10">
              <p className="text-[10px] font-black text-indigo-400 uppercase tracking-widest mb-3 flex items-center gap-2">
                <Download size={12} /> Скачать отчёт
              </p>
              <div className="flex flex-wrap gap-3 items-end">
                <div>
                  <label className="text-[9px] font-bold text-slate-500 uppercase block mb-1">Период</label>
                  <select
                    value={exportPeriod}
                    onChange={(e) => setExportPeriod(e.target.value)}
                    className="bg-slate-900 border border-white/10 rounded-xl px-3 py-2 text-xs text-white outline-none focus:border-indigo-500"
                  >
                    <option value="today">За сегодня</option>
                    <option value="week">За неделю</option>
                    <option value="month">За месяц</option>
                    <option value="3months">За 3 месяца</option>
                  </select>
                </div>
                {(exportPeriod === 'month' || exportPeriod === '3months') && (
                  <div>
                    <label className="text-[9px] font-bold text-slate-500 uppercase block mb-1">Месяц</label>
                    <input
                      type="month"
                      value={exportMonth}
                      onChange={(e) => setExportMonth(e.target.value)}
                      className="bg-slate-900 border border-white/10 rounded-xl px-3 py-2 text-xs text-white outline-none"
                    />
                  </div>
                )}
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => handleExport('excel')}
                    disabled={!!exportLoading}
                    className="flex items-center gap-2 px-4 py-2 rounded-xl bg-emerald-600/20 hover:bg-emerald-600/40 border border-emerald-500/30 text-emerald-400 text-[10px] font-black uppercase disabled:opacity-50"
                  >
                    {exportLoading === 'excel' ? <Loader2 size={14} className="animate-spin" /> : <FileSpreadsheet size={14} />}
                    Excel
                  </button>
                  <button
                    type="button"
                    onClick={() => handleExport('pdf')}
                    disabled={!!exportLoading}
                    className="flex items-center gap-2 px-4 py-2 rounded-xl bg-rose-600/20 hover:bg-rose-600/40 border border-rose-500/30 text-rose-400 text-[10px] font-black uppercase disabled:opacity-50"
                  >
                    {exportLoading === 'pdf' ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />}
                    PDF
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Модалка */}
      {showAddModal && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 overflow-y-auto">
          <div className="absolute inset-0 bg-black/90 backdrop-blur-md" onClick={() => setShowAddModal(false)} />
          <form 
            onSubmit={handleAddManager} 
            className="relative bg-[#1e293b] border border-white/10 w-full max-w-md rounded-2xl sm:rounded-[3rem] p-6 sm:p-8 md:p-10 shadow-2xl animate-in zoom-in-95 duration-200 my-4"
          >
            <div className="flex justify-between items-center mb-8">
              <h2 className="text-2xl font-black text-white italic uppercase">Новый сотрудник</h2>
              <button type="button" onClick={() => setShowAddModal(false)} className="text-slate-500 hover:text-white"><X size={20}/></button>
            </div>

            <div className="space-y-4">
              <input required type="text" className="w-full bg-slate-900 border border-white/5 rounded-xl px-5 py-4 text-xs outline-none focus:border-indigo-500 text-white" placeholder="ИМЯ" onChange={e => setNewManager({...newManager, name: e.target.value})} />
              <input required type="email" className="w-full bg-slate-900 border border-white/5 rounded-xl px-5 py-4 text-xs outline-none focus:border-indigo-500 text-white" placeholder="EMAIL" onChange={e => setNewManager({...newManager, email: e.target.value})} />
              <input required type="password" placeholder="ПАРОЛЬ" className="w-full bg-slate-900 border border-white/5 rounded-xl px-5 py-4 text-xs outline-none focus:border-indigo-500 text-white" onChange={e => setNewManager({...newManager, password: e.target.value})} />
              <select
                value={newManager.green_api_slot}
                onChange={e => setNewManager({...newManager, green_api_slot: e.target.value})}
                className="w-full bg-slate-900 border border-white/5 rounded-xl px-5 py-4 text-xs outline-none focus:border-indigo-500 text-white"
              >
                <option value="">Без WhatsApp-номера</option>
                {[1, 2, 3, 4, 5].map(slot => (
                  <option key={slot} value={slot}>WhatsApp-номер №{slot}</option>
                ))}
              </select>
            </div>

            <button type="submit" disabled={addManagerLoading} className="w-full bg-indigo-600 hover:bg-indigo-500 disabled:bg-slate-700 text-white py-4 rounded-xl font-black text-[10px] uppercase tracking-[0.2em] mt-8 transition-all flex items-center justify-center gap-2">
              {addManagerLoading ? <Loader2 className="animate-spin" size={18} /> : null}
              Создать аккаунт
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

export default ManagersPage;
